import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { SVGProps } from "react";
import { api } from "@/lib/api";
import { pluralise } from "@/lib/format";
import { useApiMutation } from "@/lib/mutations";
import { useFeatureGate } from "@/lib/gating";
import { Padlock, PadlockButton } from "@/components/padlock";
import { IconAlert, IconCheck, IconClose, IconExport, IconImport, IconSheet } from "@/components/icons";
import { BrandSpinner } from "@/components/spinner";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  Checkbox,
  EmptyState,
  ErrorState,
  PageHeader,
  Spinner,
  StatTile,
  Table,
} from "@/components/ui";
import { InfoTip } from "@/components/info-tip";
import { HistoryPanel } from "./history-panel";

/**
 * Import and export.
 *
 * Two halves of one promise. An agency arriving from a spreadsheet should not have to retype its
 * portfolio, and an agency that wants to leave — or simply to reconcile in a spreadsheet — should
 * be able to take everything with it.
 *
 * **The screen is built around the preview.** Choosing a file does not import it: it produces a
 * report of what would happen, row by row, with the line numbers the spreadsheet shows, and the
 * import only runs when that report has been read and accepted. A bad import is worse than no
 * import, and the only way to make one safe is to let somebody see it before it happens.
 *
 * A row that cannot be read is reported and skipped — the rest of the file still lands. That works
 * because a second run is safe: every kind is matched on the key the product already enforces
 * uniqueness on, so correcting three cells and importing the whole file again updates what is
 * there rather than doubling it.
 *
 * **Everything at once travels as an archive**, because these are ten unrelated shapes and a single
 * CSV holding all of them is a file no spreadsheet can open. The export writes one; the import
 * reads one back, and the two tick lists are deliberate mirror images — an export starts with
 * everything ticked, an import with nothing.
 */

/* ------------------------------------------------------------------------- icons */

/*
  One mark the set did not have.

  Same 24-grid, 1.75 stroke and rounded caps as `components/icons.tsx`, and it belongs in that
  file — it is drawn here only because that file is owned elsewhere at the time of writing.
  Moving it is a copy and a change of import; nothing else refers to it.
*/

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

/** A crate with a band across the lid. An archive is a parcel of files, not a document. */
function IconArchive({ size = 18, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      <rect x="3" y="3.5" width="18" height="5" rx="1.5" />
      <path d="M5 8.5v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-10" />
      <path d="M10 13h4" />
    </svg>
  );
}

/**
 * Handing the reader a file the browser made, rather than one the server sent.
 *
 * The rows to correct are already in the report — asking the server for them a second time would
 * be a second read of the same file, and the two answers could differ if anything had changed in
 * between. The byte-order mark goes on the front for the reason every download in this product
 * carries one: without it Excel reads a UTF-8 file in the machine's own codepage and hands back
 * mangled names.
 */
function downloadText(name: string, text: string): void {
  const blob = new Blob([`﻿${text}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------------- types */

interface ExportKind {
  kind: string;
  label: string;
  blurb: string;
  columns: string[];
}

interface ImportColumn {
  key: string;
  header: string;
  required?: boolean;
  example: string;
  note?: string;
}

interface ImportKind {
  kind: string;
  label: string;
  matchedOn: string;
  columns: ImportColumn[];
  template: string;
}

interface RowError {
  column: string | null;
  message: string;
}

interface RowPlan {
  line: number;
  action: "create" | "update" | "error";
  label: string;
  errors: RowError[];
  matchedId: string | null;
  matchedLabel: string | null;
}

interface ImportReport {
  kind: string;
  label: string;
  matchedOn: string;
  columns: { recognised: string[]; ignored: string[] };
  summary: { rows: number; create: number; update: number; error: number };
  limitNote: string | null;
  /** The bad rows as a file to correct and send back, or null when every row was readable. */
  retry: string | null;
  retryTruncated: boolean;
  rows: RowPlan[];
  truncated: boolean;
  committed: boolean;
  created?: number;
  updated?: number;
  failed?: number;
}

/* -------------------------------------------------------------------- the screen */

export function DataTransferPage() {
  return (
    <div>
      <PageHeader
        title="Import and export"
        subtitle="Bring a portfolio in from a spreadsheet, or take every list out as one."
        description="Every list this agency owns can be downloaded as a CSV file, or all of them together as one archive. Every one of them can be brought back the same way — clients, properties, units, tenancies, invoices, payments and the rest — and a file is read and reported on before anything is written, so you can see exactly what will happen and correct it first."
      />

      <PlanNotice />

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)]">
        <ExportPanel />
        <ImportPanel />
      </div>

      <HistoryPanel />
    </div>
  );
}

/**
 * Moving records in or out is a paid feature, and the screen has to say so before it is used.
 *
 * The API refuses with a 402 that names the plan — but arriving at that refusal by choosing a file,
 * ticking eight lists and pressing a button is a poor way to be told. The answer comes back with
 * the lists themselves, so it is known on load.
 *
 * **The words are the server's.** `gate.message` already names the feature, the plan that includes
 * it and the plan this agency is on; the sentence after it is the reassurance the padlock is
 * built on, and the modal behind the button says the rest. Nothing here compares a plan code, and
 * nothing here decides that the feature is shut — the presence of the gate *is* that decision. See
 * `lib/gating.ts`.
 */
function PlanNotice() {
  const gate = useFeatureGate("data_transfer");
  if (!gate) return null;

  return (
    <div
      className="mb-5 flex flex-wrap items-center gap-3 rounded-[var(--radius-surface)] border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800"
      data-transfer-locked
      role="status"
    >
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{gate.message}</p>
        <p className="mt-0.5">
          Every list below is still shown, so you can see exactly what you would be able to take out
          or bring in. Your records remain yours and nothing has been removed — the files simply
          cannot be produced or read until the plan is raised.
        </p>
      </div>
      <PadlockButton gate={gate} variant="primary">
        See the plans
      </PadlockButton>
    </div>
  );
}

/* ------------------------------------------------------------------------ export */

function ExportPanel() {
  /*
    The same gate the notice above reads, asked again here rather than passed down.

    `/data/exports` serves it beside the lists for exactly this: the options are drawn in full,
    padlocked, instead of disappearing — a feature nobody can see is a feature nobody buys, and an
    agency deciding whether to pay has to be able to see what it would be paying for.
  */
  const gate = useFeatureGate("data_transfer");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["data-exports"],
    queryFn: () => api.get<{ exports: ExportKind[] }>("/data/exports"),
  });

  /*
    Everything is ticked to begin with.

    "Export" almost always means "all of it" — an agency taking a copy for their accountant, or
    leaving. Starting from nothing would make the common case a dozen clicks, and the rare case
    (one list) is one untick away from here. Import is the mirror image and starts from nothing,
    because writing into a live book is not something anybody should arrive at by default.
  */
  const [chosen, setChosen] = useState<Set<string> | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const kinds = data?.exports ?? [];
  const picked = chosen ?? new Set(kinds.map((entry) => entry.kind));
  const all = kinds.length > 0 && picked.size === kinds.length;
  const some = picked.size > 0 && !all;

  const toggle = (kind: string) => {
    const next = new Set(picked);
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    setChosen(next);
  };

  /*
    The archive is fetched rather than linked, because it is a POST — the list of what to include
    does not belong in an address somebody might bookmark and then be surprised by. A blob URL
    revoked straight after keeps the file out of memory once the browser has it.
  */
  const download = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const response = await fetch("/api/v1/data/export/bundle", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ kinds: [...picked] }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error ?? "That download could not be prepared.");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = (response.headers.get("content-disposition") ?? "").match(/filename="([^"]+)"/)?.[1] ?? "rentorng.zip";
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (thrown) {
      setProblem(thrown instanceof Error ? thrown.message : "That download could not be prepared.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardTitle
        hint="A file holds exactly the rows you can see on the matching screen — the same query, without a page limit. An agent working on part of the portfolio therefore downloads that part and no more."
      >
        <span className="inline-flex items-center gap-2">
          <IconExport size={17} />
          Export
          <Padlock gate={gate} label="Locked" />
        </span>
      </CardTitle>

      {isLoading ? <Spinner /> : null}
      {error ? <ErrorState error={error} retry={() => refetch()} /> : null}

      {data ? (
        <>
          {kinds.length > 0 ? (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-surface)] bg-ink-50 px-4 py-3">
              <Checkbox
                label={<span className="font-semibold">Everything</span>}
                checked={all}
                indeterminate={some}
                onChange={() => setChosen(all ? new Set() : new Set(kinds.map((entry) => entry.kind)))}
                data-export-all
              />
              <p className="text-xs text-ink-500">
                {picked.size} of {kinds.length} selected
              </p>
            </div>
          ) : null}

          <div className="space-y-2">
            {kinds.map((entry) => (
              <div
                key={entry.kind}
                className="flex items-start justify-between gap-4 rounded-[var(--radius-surface)] border border-ink-200 px-4 py-3"
              >
                <Checkbox
                  checked={picked.has(entry.kind)}
                  onChange={() => toggle(entry.kind)}
                  data-export-pick={entry.kind}
                  label={
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-ink-900">
                      {entry.label}
                      <InfoTip label={`Columns in the ${entry.label} file`}>
                        <span className="font-semibold">Columns</span>
                        <br />
                        {entry.columns.join(" · ")}
                      </InfoTip>
                    </span>
                  }
                  hint={entry.blurb}
                />
                {gate ? (
                  <PadlockButton gate={gate} size="sm" className="shrink-0">
                    CSV
                  </PadlockButton>
                ) : (
                  <a
                    className="btn btn-secondary btn-sm shrink-0"
                    href={`/api/v1/data/export/${entry.kind}`}
                    data-export={entry.kind}
                  >
                    CSV
                  </a>
                )}
              </div>
            ))}
            {kinds.length === 0 ? (
              <EmptyState title="There is nothing here to export." hint="Your role does not include any of the lists this page can produce." />
            ) : null}
          </div>

          {kinds.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {gate ? (
                <PadlockButton gate={gate} variant="primary">
                  Download everything
                </PadlockButton>
              ) : (
                <Button onClick={download} disabled={busy || picked.size === 0} data-export-bundle>
                  {busy ? "Preparing…" : `Download ${picked.size === kinds.length ? "everything" : `${picked.size} ${picked.size === 1 ? "file" : "files"}`}`}
                </Button>
              )}
              <p className="text-xs text-ink-500">
                One archive holding a file for each thing ticked, and a note saying what is in it.
              </p>
            </div>
          ) : null}

          {problem ? (
            <div className="mt-3">
              <Alert tone="danger">{problem}</Alert>
            </div>
          ) : null}
        </>
      ) : null}
    </Card>
  );
}

/* ------------------------------------------------------------------------ import */

/* ------------------------------------------------ reading an archive in the browser */

/**
 * The files inside a ZIP, opened here rather than on the server.
 *
 * **Why the browser reads it at all.** `POST /data/import/bundle` takes the whole archive and does
 * the lot in one request, which is correct and stays as the fallback below — but one request can
 * say nothing at all while it is in flight. An archive of ten lists is ten passes over everything
 * already in the agency, and a screen that creeps a bar along a timer while that happens is telling
 * the reader something it does not know. So the archive is opened here and the lists are imported
 * one request each, in the order the server declares. Every step shown as finished has genuinely
 * finished, because a response came back for it.
 *
 * This is the reader from `apps/api/src/lib/zip.ts` in the shapes a browser has: a `DataView` for
 * the little-endian reads, and `DecompressionStream` for the deflated entries, which is `zlib`
 * under another name. It reads through the **central directory** for the same reason that one does
 * — that is what every other reader consults, so if the directory and the local headers ever
 * disagree this sees what a spreadsheet would see rather than what we hoped we wrote.
 *
 * A browser without `DecompressionStream`, or an archive this cannot follow, is not a failure:
 * `unreadable` is set, the checklist is drawn from what the account may import rather than from
 * what is in the file, and the one-request endpoint does the work.
 */
const EOCD_SIG = 0x06054b50;
const CENTRAL_SIG = 0x02014b50;

/* Bytes backed by a plain `ArrayBuffer`, not a shared one. `Blob` and `DataView` both refuse the
   shared kind, and `Uint8Array` alone leaves it open — which is a compile error here and nowhere
   else in the app, because nothing else in it touches raw bytes. */
type Bytes = Uint8Array<ArrayBuffer>;

/** The first four bytes of every ZIP ever written. */
function looksLikeArchive(bytes: Bytes): boolean {
  return bytes.length > 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}

async function inflateRaw(bytes: Bytes): Promise<Bytes> {
  if (typeof DecompressionStream === "undefined") throw new Error("This browser cannot open the archive itself.");
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

async function readArchive(bytes: Bytes): Promise<Array<{ name: string; text: string }>> {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  /* The end record is last but may be followed by a comment, so it is found by scanning back. The
     format caps that comment at 64KB, which bounds the scan. */
  let end = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 0xffff); i -= 1) {
    if (view.getUint32(i, true) === EOCD_SIG) {
      end = i;
      break;
    }
  }
  if (end === -1) throw new Error("That file is not an archive.");

  const count = view.getUint16(end + 10, true);
  let at = view.getUint32(end + 16, true);
  const decoder = new TextDecoder("utf-8");
  const entries: Array<{ name: string; text: string }> = [];

  for (let n = 0; n < count; n += 1) {
    if (view.getUint32(at, true) !== CENTRAL_SIG) throw new Error("That archive is damaged.");
    const method = view.getUint16(at + 10, true);
    const compressedSize = view.getUint32(at + 20, true);
    const nameLength = view.getUint16(at + 28, true);
    const extraLength = view.getUint16(at + 30, true);
    const commentLength = view.getUint16(at + 32, true);
    const localAt = view.getUint32(at + 42, true);
    const name = decoder.decode(bytes.subarray(at + 46, at + 46 + nameLength));

    /* The local header's own name and extra lengths, not the central record's — they are allowed
       to differ, and assuming otherwise reads the file from a few bytes inside itself. */
    const dataAt = localAt + 30 + view.getUint16(localAt + 26, true) + view.getUint16(localAt + 28, true);
    const body = bytes.subarray(dataAt, dataAt + compressedSize);
    entries.push({ name, text: decoder.decode(method === 8 ? await inflateRaw(body) : body) });
    at += 46 + nameLength + extraLength + commentLength;
  }

  return entries;
}

/** Base64, in chunks. `String.fromCharCode(...bytes)` over a megabyte overflows the call stack. */
function toBase64(bytes: Bytes): string {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Which list a file in the archive holds, by its name — `properties.csv`, or a folder and then it. */
function kindOfEntry(name: string): string | null {
  const match = /(?:^|\/)([a-z-]+)\.csv$/i.exec(name);
  return match ? match[1].toLowerCase() : null;
}

interface Archive {
  fileName: string;
  /** The whole thing, base64, for the one-request fallback. */
  base64: string;
  /** kind → the CSV inside it. Empty when the archive could not be opened here. */
  files: Map<string, string>;
  /** Entries this account has no import for — named rather than silently dropped. */
  extras: string[];
  /** Why it could not be opened here, when it could not. Null on the ordinary path. */
  unreadable: string | null;
}

/* ------------------------------------------------------------------ the run itself */

/**
 * What one list is doing.
 *
 * `stopped` is not `waiting`. A list never attempted because an earlier one could not be read is a
 * different thing from one whose turn has not come, and a reader who cannot tell them apart will
 * sit watching a run that is already over.
 */
type StepState = "waiting" | "running" | "done" | "failed" | "stopped";

interface Step {
  kind: string;
  label: string;
  state: StepState;
  report: ImportReport | null;
  /** Why the whole file was refused. Only ever set on a `failed` step. */
  message: string | null;
}

/** A step's figures, in the tense the run is in — planned before it runs, actual afterwards. */
function countsOf(step: Step, committed: boolean) {
  const report = step.report;
  if (!report) return { rows: 0, create: 0, update: 0, error: 0 };
  return {
    rows: report.summary.rows,
    create: committed ? (report.created ?? 0) : report.summary.create,
    update: committed ? (report.updated ?? 0) : report.summary.update,
    error: committed ? (report.failed ?? 0) : report.summary.error,
  };
}

function ImportPanel() {
  const queryClient = useQueryClient();
  /*
    **The controls stay on the screen and are held**, rather than being removed.

    Held rather than removed is the point of the whole reversal: somebody has to be able to see the
    checklist of everything they could bring in before they can want it. The padlock beside the
    heading says why they are held, and pressing it says what would open them.
  */
  const gate = useFeatureGate("data_transfer");
  const allowed = !gate;

  const [kind, setKind] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [csv, setCsv] = useState<string | null>(null);
  const [archive, setArchive] = useState<Archive | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /*
    Nothing is ticked to begin with, here and on every file chosen afterwards.

    The mirror image of the export, and deliberately not symmetrical in spirit. **An export takes a
    copy and cannot hurt anybody**, so the helpful default is all of it. **An import writes into a
    live book**, and a screen that arrives pre-ticked invites somebody to press the button without
    reading it — which is precisely the moment this feature can do damage. So every list here is a
    decision somebody made, and the master box makes "all of it" one press for when that is meant.

    This is a binding instruction from the user, not a preference: export all ticked, import none.
    `chooseFile` clears the set again for the same reason — a second archive must not inherit the
    ticks from the first.
  */
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const [steps, setSteps] = useState<Step[] | null>(null);
  /** What the steps on screen describe — a reading, or a writing. */
  const [ran, setRan] = useState<"preview" | "commit" | null>(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["data-imports"],
    queryFn: () => api.get<{ imports: ImportKind[] }>("/data/imports"),
  });

  const kinds = useMemo(() => data?.imports ?? [], [data]);
  const chosen = useMemo(() => kinds.find((entry) => entry.kind === (kind ?? kinds[0]?.kind)), [kinds, kind]);

  /*
    The order the lists are imported in, and it is the server's, not this screen's.

    `/data/imports` answers in the order `IMPORTS` declares in `apps/api/src/routes/data.ts`, and
    that order is load-bearing: a unit needs its property to exist first. So the list is used as it
    arrives — never sorted, and never taken from the archive, whose entries any tool at all is free
    to reorder. An archive holding `units.csv` before `properties.csv` still imports the properties
    first.
  */
  const declaredOrder = useMemo(() => kinds.map((entry) => entry.kind), [kinds]);
  const labelOf = (of: string) => kinds.find((entry) => entry.kind === of)?.label ?? of;

  /** The lists this archive holds and this account may write, in the declared order. */
  const inArchive = useMemo(
    () => (archive ? declaredOrder.filter((entry) => archive.files.has(entry)) : []),
    [archive, declaredOrder],
  );
  /* When the archive could not be opened here, the checklist is everything importable instead: the
     server finds the files inside and skips what is not there. */
  const offered = archive?.unreadable ? declaredOrder : inArchive;

  const allPicked = offered.length > 0 && offered.every((entry) => picked.has(entry));
  const somePicked = offered.some((entry) => picked.has(entry)) && !allPicked;

  const toggle = (entry: string) => {
    const next = new Set(picked);
    if (next.has(entry)) next.delete(entry);
    else next.add(entry);
    setPicked(next);
  };

  /**
   * The preview and the import are the same request with one flag between them.
   *
   * Deliberately so: two code paths would eventually disagree, and the day they do, the report
   * somebody read stops describing the import that ran.
   */
  const run = useApiMutation({
    mutationFn: (input: { kind: string; csv: string; commit: boolean }) =>
      api.post<ImportReport>(`/data/import/${input.kind}`, { csv: input.csv, commit: input.commit }),
    invalidates: [["properties"], ["units"], ["parties"], ["dashboard"]],
    onSuccess: (result) => setReport(result),
  });

  function clearRun() {
    setSteps(null);
    setRan(null);
    setRunError(null);
  }

  /*
    A file is recognised by its bytes, not by its name.

    Every ZIP begins `PK\x03\x04`, and a name is not evidence: an archive arrives called
    `portfolio.zip.csv` from a mail client often enough, and several browsers hand over an empty
    MIME type for both kinds of file. Four bytes settle it and cannot be wrong.
  */
  async function chooseFile(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    clearRun();
    setReport(null);
    setReadError(null);
    setFileName(file.name);
    setCsv(null);
    setArchive(null);
    setPicked(new Set());

    let bytes: Bytes;
    try {
      bytes = new Uint8Array(await file.arrayBuffer());
    } catch {
      setReadError("That file could not be read.");
      return;
    }

    if (!looksLikeArchive(bytes)) {
      /* A byte-order mark is handled on the server, where the parser strips it — see
         `packages/core/src/csv.ts`. */
      setCsv(new TextDecoder("utf-8").decode(bytes));
      return;
    }

    const base64 = toBase64(bytes);
    try {
      const entries = await readArchive(bytes);
      const found = new Map<string, string>();
      const extras: string[] = [];
      for (const entry of entries) {
        const entryKind = kindOfEntry(entry.name);
        if (entryKind && declaredOrder.includes(entryKind)) found.set(entryKind, entry.text);
        else if (!/manifest\.txt$/i.test(entry.name)) extras.push(entry.name);
      }
      setArchive({ fileName: file.name, base64, files: found, extras, unreadable: null });
    } catch (thrown) {
      /* Not an error the reader has to act on. The server can open it; what is lost is the
         list-by-list progress, and the notice below says so rather than hiding it. */
      setArchive({
        fileName: file.name,
        base64,
        files: new Map(),
        extras: [],
        unreadable: thrown instanceof Error ? thrown.message : "This browser could not open the archive.",
      });
    }
  }

  function reset() {
    setCsv(null);
    setArchive(null);
    setFileName(null);
    setReport(null);
    setReadError(null);
    setPicked(new Set());
    clearRun();
    run.reset();
    if (fileInput.current) fileInput.current.value = "";
  }

  const patch = (of: string, change: Partial<Step>) =>
    setSteps((current) => (current ?? []).map((step) => (step.kind === of ? { ...step, ...change } : step)));

  /**
   * The archive, one list at a time, in the declared order.
   *
   * **The loop stops at the first list that cannot be read at all**, and the lists after it are
   * marked as never attempted rather than left looking pending. That is stricter than it has to be
   * for a list that depends on nothing — clients would import perfectly well after a broken units
   * file — and it is still the right answer: an archive is one thing, the order exists precisely
   * because a list further down may need one above it, and carrying on past a file that could not
   * be opened writes rows whose references were never created. A bad *row* is a different matter
   * and stops nothing; that is the server's rule and it is untouched here.
   *
   * What was already written stays written. Nothing is rolled back, for the reason the endpoint
   * gives: every kind is matched on its natural key, so correcting the broken file and running the
   * whole archive again updates what landed rather than doubling it.
   */
  async function runArchive(commit: boolean, initial: Step[]) {
    if (!archive || !initial.some((step) => step.state === "waiting")) return;

    setRunError(null);
    setRan(commit ? "commit" : "preview");
    setRunning(true);
    setSteps(initial);

    for (const step of initial) {
      /*
        A commit carries the reading's own lines forward untouched — the file that could not be
        opened stays on screen as the one that stopped the run. Rebuilding the list from only the
        lists about to be written would quietly delete the explanation at the moment somebody most
        needs it, which is immediately after pressing Import.
      */
      if (step.state !== "waiting") continue;
      patch(step.kind, { state: "running" });
      try {
        const result = await api.post<ImportReport>(`/data/import/${step.kind}`, {
          csv: archive.files.get(step.kind) ?? "",
          commit,
        });
        patch(step.kind, { state: "done", report: result });
      } catch (thrown) {
        const message = thrown instanceof Error ? thrown.message : "That file was not accepted.";
        setSteps((current) =>
          (current ?? []).map((one) =>
            one.kind === step.kind
              ? { ...one, state: "failed", message }
              : one.state === "waiting"
                ? { ...one, state: "stopped" }
                : one,
          ),
        );
        break;
      }
    }

    setRunning(false);
    if (commit) {
      for (const key of [["properties"], ["units"], ["parties"], ["dashboard"]]) {
        await queryClient.invalidateQueries({ queryKey: key });
      }
    }
  }

  /**
   * The fallback: the whole archive in one request.
   *
   * Used only when this browser could not open the archive itself. There is no list-by-list
   * progress to show because there is none to be had — one request is in flight and it will either
   * come back or not — so the screen says it is reading the archive and means exactly that, rather
   * than animating a fraction it cannot know.
   */
  async function runBundle(commit: boolean) {
    if (!archive) return;
    const wanted = offered.filter((entry) => picked.has(entry));
    if (!wanted.length) return;

    setRunError(null);
    setRan(commit ? "commit" : "preview");
    setRunning(true);
    setSteps(
      wanted.map((entry) => ({ kind: entry, label: labelOf(entry), state: "running", report: null, message: null })),
    );

    try {
      const result = await api.post<{ committed: boolean; results: ImportReport[] }>("/data/import/bundle", {
        archive: archive.base64,
        kinds: wanted,
        commit,
      });
      const byKind = new Map(result.results.map((entry) => [entry.kind, entry]));
      setSteps(
        wanted.map((entry) => ({
          kind: entry,
          label: labelOf(entry),
          /* A list the archive did not hold comes back absent rather than empty, and saying it was
             not there beats showing a row of zeroes. */
          state: byKind.has(entry) ? "done" : "stopped",
          report: byKind.get(entry) ?? null,
          message: null,
        })),
      );
    } catch (thrown) {
      setSteps(null);
      setRunError(thrown instanceof Error ? thrown.message : "That archive was not accepted.");
    } finally {
      setRunning(false);
      if (commit) await queryClient.invalidateQueries();
    }
  }

  /** A fresh line per list, in the declared order, for a reading that has not happened yet. */
  const waitingFor = (entries: string[]): Step[] =>
    entries.map((entry) => ({ kind: entry, label: labelOf(entry), state: "waiting", report: null, message: null }));

  const startPreview = () => {
    const wanted = offered.filter((entry) => picked.has(entry) && (archive?.files.has(entry) ?? false));
    return archive?.unreadable ? runBundle(false) : runArchive(false, waitingFor(wanted));
  };

  /**
   * What a commit covers: every list that was read, whatever the reading said about its rows.
   *
   * **Not "every list with something to write", which is the obvious rule and the wrong one.** A
   * reading writes nothing, so while it is happening the properties are not there yet — and every
   * unit in the archive is therefore reported as naming a property that cannot be found. Dropping
   * those lists from the import because the reading found nothing to do would mean an archive could
   * never bring a portfolio in at all: the buildings would land and the doors never would.
   *
   * The rows are read again as each list is imported, by which time the list above it has been
   * written, and they land then. The note under the button says so, because a reading that shows
   * four units failing and an import that adds four units needs explaining before it happens rather
   * than afterwards.
   */
  const committable = (steps ?? []).filter((step) => step.state === "done");
  const stopper = (steps ?? []).find((step) => step.state === "failed");

  const previewed = report && !report.committed;
  const done = report?.committed;

  return (
    <Card>
      <CardTitle hint="Nothing is written when a file is chosen. The file is read, checked row by row and reported back; the import runs only when you press the button underneath that report.">
        <span className="inline-flex items-center gap-2">
          <IconImport size={17} />
          Import
          <Padlock gate={gate} label="Locked" />
        </span>
      </CardTitle>

      {isLoading ? <Spinner label="Loading what can be imported" /> : null}
      {error ? <ErrorState error={error} retry={() => refetch()} /> : null}

      {kinds.length === 0 && !isLoading ? (
        <EmptyState
          title="There is nothing here to import."
          hint="Your role does not include editing any of the lists this page can fill."
        />
      ) : null}

      {kinds.length > 0 && chosen ? (
        <div className="space-y-5">
          {/*
            The picker belongs to the single-file path alone. An archive names its own lists inside
            itself, and asking somebody what a file is when the file already says so is a question
            with a wrong answer available.
          */}
          {archive ? null : (
            <>
              <div>
                <span className="label">What are you importing?</span>
                <div className="flex flex-wrap gap-2">
                  {kinds.map((entry) => (
                    <Button
                      key={entry.kind}
                      size="sm"
                      variant={entry.kind === chosen.kind ? "primary" : "secondary"}
                      data-kind={entry.kind}
                      onClick={() => {
                        setKind(entry.kind);
                        reset();
                      }}
                    >
                      {entry.label}
                    </Button>
                  ))}
                </div>
              </div>

              <ColumnReference spec={chosen} />
            </>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileInput}
              id="import-file"
              name="csv"
              type="file"
              accept=".csv,.zip,text/csv,application/zip"
              className="field max-w-sm"
              onChange={(event) => void chooseFile(event.target.files)}
            />
            {archive ? null : gate ? (
              /* Shut, not dead. A disabled button says "not now" and gives no way to ask why; this
                 one keeps its own word — Preview — and opens the modal that says what would allow
                 it. The words stay the words, because what somebody is missing is the argument. */
              <PadlockButton gate={gate} variant="primary">
                Preview
              </PadlockButton>
            ) : (
              <Button
                id="import-preview"
                disabled={!csv || run.isPending}
                onClick={() => csv && run.mutate({ kind: chosen.kind, csv, commit: false })}
              >
                {run.isPending && !previewed ? "Reading…" : "Preview"}
              </Button>
            )}
            {fileName ? (
              <Button variant="ghost" size="sm" onClick={reset}>
                <IconClose size={15} /> Clear
              </Button>
            ) : null}
          </div>

          <p className="-mt-3 text-xs leading-relaxed text-ink-500">
            One list as a CSV file, or every list at once as the archive this page exports. An archive
            is read the same way a single file is: list by list, reported first, and written only when
            you say so.
          </p>

          {readError ? <Alert tone="danger">{readError}</Alert> : null}
          {runError ? (
            <Alert tone="danger" title="That archive was not accepted">
              {runError}
            </Alert>
          ) : null}
          {run.errorMessage ? (
            <Alert tone="danger" title="That file was not accepted">
              {run.errorMessage}
            </Alert>
          ) : null}

          {/* ------------------------------------------------------- the archive path */}

          {archive ? (
            <div className="space-y-4" data-archive={archive.fileName}>
              <div className="rounded-[var(--radius-surface)] border border-ink-200 bg-ink-50 px-4 py-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-ink-900">
                  <IconArchive size={16} />
                  {archive.fileName}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-ink-600">
                  {archive.unreadable
                    ? "This browser could not open the archive, so the whole file is sent at once and the lists inside it are matched by name. The result is the same; only the progress below is coarser."
                    : `${pluralise(inArchive.length, "list")} inside this archive can be imported. Tick what should be read, and it is checked before anything is written.`}
                </p>
                {archive.extras.length ? (
                  <p className="mt-2 text-xs leading-relaxed text-ink-500">
                    Also inside: {archive.extras.join(", ")}. There is no import for
                    {archive.extras.length === 1 ? " it" : " those"} yet, so
                    {archive.extras.length === 1 ? " it is" : " they are"} left alone.
                  </p>
                ) : null}
              </div>

              {offered.length === 0 ? (
                <EmptyState
                  title="Nothing in this archive can be imported."
                  /* Named from what the server offered rather than typed. This line used to name
                     three files when the archive holds ten, which is how somebody ends up
                     believing their tenancies and invoices cannot come back. */
                  hint={`The files inside must be named after what they hold — ${kinds
                    .map((entry) => `${entry.kind}.csv`)
                    .join(", ")}.`}
                />
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-surface)] bg-ink-50 px-4 py-3">
                    <Checkbox
                      label={<span className="font-semibold">Everything in this archive</span>}
                      checked={allPicked}
                      indeterminate={somePicked}
                      disabled={running || !allowed}
                      onChange={() => setPicked(allPicked ? new Set() : new Set(offered))}
                      data-import-all
                    />
                    <p className="text-xs text-ink-500" data-import-count>
                      {picked.size} of {offered.length} selected
                    </p>
                  </div>

                  <div className="space-y-2">
                    {offered.map((entry) => (
                      <div
                        key={entry}
                        className="flex items-start justify-between gap-4 rounded-[var(--radius-surface)] border border-ink-200 px-4 py-3"
                      >
                        <Checkbox
                          checked={picked.has(entry)}
                          disabled={running || !allowed}
                          onChange={() => toggle(entry)}
                          data-import-pick={entry}
                          label={
                            <span className="flex items-center gap-1.5 text-sm font-semibold text-ink-900">
                              {labelOf(entry)}
                              <InfoTip label={`How a row in ${labelOf(entry)} is matched`}>
                                <span className="font-semibold">Matched on</span>
                                <br />
                                {kinds.find((one) => one.kind === entry)?.matchedOn ?? ""} — a row matching
                                something already here is updated rather than added a second time.
                              </InfoTip>
                            </span>
                          }
                          hint={`${entry}.csv`}
                        />
                        {/* Its place in the order the lists are written in, which is not the order
                            they sit in the archive. A unit needs its property. */}
                        <span className="shrink-0 text-xs text-ink-400" data-import-place={entry}>
                          {declaredOrder.indexOf(entry) + 1} of {declaredOrder.length}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    {gate ? (
                      <PadlockButton gate={gate} variant="primary">
                        {`Check ${pluralise(picked.size, "list")}`}
                      </PadlockButton>
                    ) : (
                      <Button
                        id="archive-preview"
                        disabled={running || picked.size === 0}
                        onClick={() => void startPreview()}
                        data-archive-preview
                      >
                        {running && ran === "preview" ? "Checking…" : `Check ${pluralise(picked.size, "list")}`}
                      </Button>
                    )}
                    <span className="text-xs text-ink-500">
                      Every list is read and reported on first. This button writes nothing.
                    </span>
                  </div>
                </>
              )}

              {steps ? <RunProgress steps={steps} running={running} committed={ran === "commit"} /> : null}

              {steps && !running && ran === "preview" ? (
                <div className="flex flex-wrap items-center gap-3 border-t border-ink-200 pt-4">
                  <Button
                    id="archive-commit"
                    disabled={committable.length === 0}
                    onClick={() =>
                      void (archive?.unreadable
                        ? runBundle(true)
                        : runArchive(
                            true,
                            (steps ?? []).map((step) =>
                              committable.includes(step)
                                ? { ...step, state: "waiting" as StepState, report: null, message: null }
                                : step,
                            ),
                          ))
                    }
                    data-archive-commit
                  >
                    {`Import ${pluralise(committable.length, "list")}`}
                  </Button>
                  <Button variant="secondary" onClick={reset}>
                    Cancel
                  </Button>
                  <span className="text-xs leading-relaxed text-ink-500">
                    Nothing has been written yet.
                    {stopper
                      ? ` ${stopper.label} could not be read, so the run stopped there; only the lists above it will be imported.`
                      : ""}
                  </span>
                  {committable.length > 1 ? (
                    <p className="w-full text-xs leading-relaxed text-ink-500" data-archive-caveat>
                      A row naming something from a list above it — a unit and the property it is in —
                      is reported here as not found, because the reading writes nothing and the
                      property is not there yet. Those rows are read again as their own list is
                      imported, by which time it is, and they land then.
                    </p>
                  ) : null}
                </div>
              ) : null}

              {steps && !running && ran === "commit" ? (
                <div className="flex flex-wrap items-center gap-3 border-t border-ink-200 pt-4">
                  <Button variant="secondary" onClick={reset}>
                    Import another file
                  </Button>
                  {stopper ? (
                    <span className="text-xs leading-relaxed text-ink-500">
                      The lists above {stopper.label} were imported and remain imported. Correct{" "}
                      {stopper.kind}.csv and import the same archive again — a list that has already
                      landed is updated rather than added twice.
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* --------------------------------------------------- the single-file path */}

          {archive || !report ? null : <Report report={report} />}

          {!archive && previewed ? (
            <div className="flex flex-wrap items-center gap-3 border-t border-ink-200 pt-4">
              <Button
                id="import-commit"
                disabled={
                  run.isPending || report.summary.create + report.summary.update === 0 || Boolean(report.limitNote)
                }
                onClick={() => csv && run.mutate({ kind: chosen.kind, csv, commit: true })}
              >
                {run.isPending
                  ? "Importing…"
                  : `Import ${report.summary.create + report.summary.update} ${
                      report.summary.create + report.summary.update === 1 ? "row" : "rows"
                    }`}
              </Button>
              <Button variant="secondary" onClick={reset}>
                Cancel
              </Button>
              <span className="text-xs text-ink-500">
                Nothing has been written yet.
                {report.summary.error > 0
                  ? ` The ${report.summary.error} ${
                      report.summary.error === 1 ? "row" : "rows"
                    } listed as a problem will be left out; correct them and import the same file again.`
                  : ""}
              </span>
            </div>
          ) : null}

          {!archive && done ? (
            <div className="flex flex-wrap items-center gap-3 border-t border-ink-200 pt-4">
              <Button variant="secondary" onClick={reset}>
                Import another file
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

/* ------------------------------------------------------------------ the progress */

/**
 * A line per list, moving as the run does.
 *
 * **Every state here is one that actually happened.** A list is `running` because its request is in
 * flight, and `done` because a response came back for it — there is no timer, no eased fraction and
 * no percentage invented to fill the gap. The bar's width is finished lists over total lists, which
 * is a number this screen knows for certain.
 *
 * The waiting animation is the product's own — `BrandSpinner`, whose drawing and rules live in
 * `packages/core/src/brand.ts` so that the three applications cannot drift into three different
 * animations. It already stands still for anybody who has asked for less movement; the bar below is
 * told to as well, because a width that slides is movement whatever is drawing it.
 */
function RunProgress({ steps, running, committed }: { steps: Step[]; running: boolean; committed: boolean }) {
  const [open, setOpen] = useState<string | null>(null);

  const finished = steps.filter((step) => step.state === "done" || step.state === "failed").length;
  const current = steps.find((step) => step.state === "running");
  const totals = steps.reduce(
    (sum, step) => {
      const counts = countsOf(step, committed);
      return {
        rows: sum.rows + counts.rows,
        create: sum.create + counts.create,
        update: sum.update + counts.update,
        error: sum.error + counts.error,
      };
    },
    { rows: 0, create: 0, update: 0, error: 0 },
  );

  const verb = committed ? "importing" : "checking";
  const headline = running
    ? current
      ? `${finished} of ${steps.length} · ${verb} ${current.label.toLowerCase()}`
      : `${finished} of ${steps.length} · reading the archive`
    : `${finished} of ${steps.length} ${steps.length === 1 ? "list" : "lists"} ${committed ? "imported" : "checked"}`;

  return (
    <div className="space-y-3 border-t border-ink-200 pt-4" data-run={committed ? "commit" : "preview"}>
      <div className="flex flex-wrap items-center gap-3">
        {running ? <BrandSpinner size="sm" label={`${committed ? "Importing" : "Checking"} your lists`} /> : null}
        <p className="text-sm font-semibold text-ink-900" data-run-headline>
          {headline}
        </p>
      </div>

      {/* The measure, and nothing else: finished lists over total lists. */}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink-100" role="presentation">
        <div
          data-run-bar
          className="h-full rounded-full bg-brand-600 transition-[width] duration-300 ease-out motion-reduce:transition-none"
          style={{ width: `${Math.round((finished / Math.max(1, steps.length)) * 100)}%` }}
        />
      </div>

      <div className="space-y-1.5">
        {steps.map((step) => {
          const counts = countsOf(step, committed);
          const bad = (step.report?.rows ?? []).filter((entry) => entry.action === "error");
          return (
            <div
              key={step.kind}
              className="rounded-[var(--radius-surface)] border border-ink-200"
              data-step={step.kind}
              data-step-state={step.state}
            >
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5">
                <span className="flex min-w-0 items-center gap-2.5 text-sm">
                  <StepMark state={step.state} label={step.label} />
                  <span className="font-semibold text-ink-900">{step.label}</span>
                  {step.state === "stopped" ? <Badge tone="muted">Not attempted</Badge> : null}
                  {step.state === "failed" ? <Badge tone="danger">Stopped here</Badge> : null}
                </span>

                {step.state === "done" && step.report ? (
                  <span className="flex flex-wrap items-center gap-3 text-xs tabular text-ink-600">
                    <span data-step-create>
                      {counts.create} {committed ? "added" : "to add"}
                    </span>
                    <span data-step-update>
                      {counts.update} {committed ? "updated" : "to update"}
                    </span>
                    <span className={counts.error ? "font-semibold text-danger-700" : ""} data-step-error>
                      {counts.error} not imported
                    </span>
                    {bad.length ? (
                      <button
                        type="button"
                        className="link"
                        data-step-open={step.kind}
                        onClick={() => setOpen(open === step.kind ? null : step.kind)}
                      >
                        {open === step.kind ? "Hide the rows" : "Show the rows"}
                      </button>
                    ) : null}
                  </span>
                ) : null}
              </div>

              {step.message ? (
                <p
                  className="border-t border-ink-200 px-4 py-2.5 text-xs leading-relaxed text-danger-700"
                  data-step-message
                >
                  {step.message}
                </p>
              ) : null}

              {step.report?.limitNote ? (
                <p className="border-t border-ink-200 px-4 py-2.5 text-xs leading-relaxed text-danger-700">
                  {step.report.limitNote}
                </p>
              ) : null}

              {/*
                The rows, only when asked for.

                Ten lists with every row on screen is a page nobody reads, and the rows that matter
                are the ones that could not be imported — those carry the line number the
                spreadsheet shows, which is the whole point of the report.
              */}
              {open === step.kind && bad.length ? (
                <div className="border-t border-ink-200 px-4 py-3" data-step-rows={step.kind}>
                  <Table
                    head={
                      <tr>
                        <th className="num">Row</th>
                        <th>Record</th>
                        <th>What is wrong</th>
                      </tr>
                    }
                  >
                    {bad.map((entry) => (
                      <tr key={entry.line} data-bad-row={entry.line}>
                        <td className="num tabular text-ink-500">{entry.line}</td>
                        <td className="font-medium text-ink-900">{entry.label || "—"}</td>
                        <td className="text-xs">
                          <ul className="space-y-0.5 text-danger-700">
                            {entry.errors.map((problem, index) => (
                              <li key={index} className="flex gap-1.5">
                                <IconAlert size={13} className="mt-0.5 shrink-0" />
                                <span>
                                  {problem.column ? <span className="font-medium">{problem.column}: </span> : null}
                                  {problem.message}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </Table>
                  {step.report?.truncated ? (
                    <p className="mt-2 text-xs text-ink-500">
                      The first {pluralise(step.report.rows.length, "row")} of the file are shown. The counts cover all
                      of it.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div data-total="rows">
          <StatTile label="Rows read" value={String(totals.rows)} />
        </div>
        <div data-total="create">
          <StatTile
            label={committed ? "Added" : "To be added"}
            value={String(totals.create)}
            tone={totals.create ? "success" : "muted"}
          />
        </div>
        <div data-total="update">
          <StatTile
            label={committed ? "Updated" : "To be updated"}
            value={String(totals.update)}
            tone={totals.update ? "info" : "muted"}
          />
        </div>
        <div data-total="error">
          <StatTile
            label={committed ? "Not imported" : "With a problem"}
            value={String(totals.error)}
            tone={totals.error ? "danger" : "muted"}
          />
        </div>
      </div>
    </div>
  );
}

/** The state of one list, as a mark. The one that is running carries the product's own animation. */
function StepMark({ state, label }: { state: StepState; label: string }) {
  if (state === "running") return <BrandSpinner size="sm" label={`Reading ${label}`} />;
  if (state === "done") {
    return (
      <span className="grid size-5 shrink-0 place-items-center rounded-full bg-brand-100 text-brand-700">
        <IconCheck size={13} />
      </span>
    );
  }
  if (state === "failed") {
    return (
      <span className="grid size-5 shrink-0 place-items-center rounded-full bg-danger-100 text-danger-700">
        <IconAlert size={13} />
      </span>
    );
  }
  /* Waiting and never-attempted are both quiet, and the badge beside them says which. A second
     colour here would compete with the one list that is actually moving. */
  return (
    <span
      aria-hidden
      className={`size-5 shrink-0 rounded-full border-2 border-ink-200 ${state === "stopped" ? "border-dashed" : ""}`}
    />
  );
}

/** What the file has to contain, with an example of each column filled in. */
function ColumnReference({ spec }: { spec: ImportKind }) {
  const [open, setOpen] = useState(false);
  const required = spec.columns.filter((column) => column.required);

  return (
    <div className="rounded-[var(--radius-surface)] border border-ink-200 bg-ink-50 px-4 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-ink-900">
          <IconSheet size={16} />
          The file
        </p>
        <button type="button" className="link text-xs" onClick={() => setOpen((value) => !value)}>
          {open ? "Hide the columns" : "Show the columns"}
        </button>
      </div>

      <p className="mt-1 text-xs leading-relaxed text-ink-600">
        The first row holds the column headings. {required.length ? (
          <>
            <span className="font-medium text-ink-800">
              {required.map((column) => column.header).join(" and ")}
            </span>{" "}
            {required.length === 1 ? "is required" : "are required"}; everything else is optional.
          </>
        ) : null}{" "}
        A row that matches something already here is updated rather than added again, matched on{" "}
        {spec.matchedOn}. Amounts may be written as 1500000, 1500000.00 or 1,500,000. Dates must be
        written as 2026-09-30 — a date like 06/07/2026 is refused rather than guessed at, because it
        could be two different days.
      </p>

      {open ? (
        <div className="mt-3 overflow-x-auto">
          <table className="table text-xs">
            <thead>
              <tr>
                <th>Column</th>
                <th>Example</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {spec.columns.map((column) => (
                <tr key={column.key}>
                  <td className="whitespace-nowrap font-medium">
                    {column.header}
                    {column.required ? <span className="text-danger-600"> *</span> : null}
                  </td>
                  <td className="whitespace-nowrap text-ink-500">{column.example || "—"}</td>
                  <td className="text-ink-500">{column.note ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

/** The report: what would happen, or what did. */
function Report({ report }: { report: ImportReport }) {
  const { summary } = report;
  /*
    The same report, before and after.

    Only the tense changes — "to be added" while it is a preview, "added" once it has run. Leaving
    it in the future tense after the fact reads as though the import is still pending, which is
    exactly the thing somebody would then do twice.
  */
  const done = report.committed;

  return (
    <div className="space-y-4 border-t border-ink-200 pt-4" data-report={report.committed ? "committed" : "preview"}>
      {report.committed ? (
        <Alert tone={report.failed ? "warn" : "success"} title="Import finished">
          <span data-committed-summary>
            {report.created} added, {report.updated} updated
            {report.failed ? `, ${report.failed} not imported` : ""}.
          </span>
        </Alert>
      ) : null}

      {/* The wrappers carry the hooks a browser test reads. `StatTile` draws the tile and does not
          forward unknown attributes, so the marker goes on the element around it. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div data-stat="rows">
          <StatTile label="Rows read" value={String(summary.rows)} />
        </div>
        <div data-stat="create">
          <StatTile
            label={done ? "Added" : "To be added"}
            value={String(done ? (report.created ?? 0) : summary.create)}
            tone={summary.create ? "success" : "muted"}
          />
        </div>
        <div data-stat="update">
          <StatTile
            label={done ? "Updated" : "To be updated"}
            value={String(done ? (report.updated ?? 0) : summary.update)}
            tone={summary.update ? "info" : "muted"}
          />
        </div>
        <div data-stat="error">
          <StatTile
            label={done ? "Not imported" : "With a problem"}
            value={String(done ? (report.failed ?? 0) : summary.error)}
            tone={summary.error ? "danger" : "muted"}
          />
        </div>
      </div>

      {report.limitNote ? (
        <Alert tone="danger" title="Your plan does not have room for this file">
          {report.limitNote}
        </Alert>
      ) : null}

      {report.retry ? (
        <Alert tone="warn" title={done ? "The rows that did not land" : "The rows that cannot be imported"}>
          <p>
            {report.retryTruncated
              ? "The first 2,000 of them are in a file of their own"
              : "They are in a file of their own"}
            , with the original columns and one extra saying what is wrong with each row. Correct
            them and import that file — the extra column is not one this reads, so it can be left
            where it is.
          </p>
          <p className="mt-2">
            <Button
              size="sm"
              variant="secondary"
              data-retry-download
              onClick={() => downloadText(`${report.kind}-to-fix.csv`, report.retry ?? "")}
            >
              Download the rows to fix
            </Button>
          </p>
        </Alert>
      ) : null}

      {report.columns.ignored.length ? (
        <Alert tone="info" title="Columns that were not recognised">
          {report.columns.ignored.join(", ")} — these are ignored. Nothing in a file decides which
          agency a record belongs to; that always comes from the account you are signed in to.
        </Alert>
      ) : null}

      {summary.rows === 0 ? (
        <EmptyState title="That file has headings but no rows." />
      ) : (
        <div className="overflow-hidden rounded-[var(--radius-surface)] border border-ink-200">
          <Table
            head={
              <tr>
                <th className="num">Row</th>
                <th>{done ? "What happened" : "What happens"}</th>
                <th>Record</th>
                <th>Notes</th>
              </tr>
            }
          >
            {report.rows.map((row) => (
              <tr key={row.line} data-row={row.line} data-action={row.action}>
                <td className="num tabular text-ink-500">{row.line}</td>
                <td>
                  {row.action === "create" ? <Badge tone="success">{done ? "Added" : "Add"}</Badge> : null}
                  {row.action === "update" ? <Badge tone="info">{done ? "Updated" : "Update"}</Badge> : null}
                  {row.action === "error" ? <Badge tone="danger">{done ? "Not imported" : "Cannot import"}</Badge> : null}
                </td>
                <td className="font-medium text-ink-900">
                  {row.label}
                  {row.matchedLabel && row.matchedLabel !== row.label ? (
                    <span className="block text-xs font-normal text-ink-500">matches {row.matchedLabel}</span>
                  ) : null}
                </td>
                <td className="text-xs">
                  {row.errors.length ? (
                    <ul className="space-y-0.5 text-danger-700">
                      {row.errors.map((problem, index) => (
                        <li key={index} className="flex gap-1.5">
                          <IconAlert size={13} className="mt-0.5 shrink-0" />
                          <span>
                            {problem.column ? <span className="font-medium">{problem.column}: </span> : null}
                            {problem.message}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-ink-400">
                      <IconCheck size={13} /> {done ? "Done" : "Ready"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        </div>
      )}

      {report.truncated ? (
        <p className="text-xs text-ink-500">
          The first {pluralise(report.rows.length, "row")} are shown. The counts above cover the whole
          file.
        </p>
      ) : null}
    </div>
  );
}

